import { SendOutlined } from "@ant-design/icons";
import { Avatar, Button, Mentions, Select, Spin } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getChat,
  markChatAsRead,
  sendMessage,
  deleteMessage,
} from "services/SauceService";
import { UserState } from "redux-context/user";
import { useSelector } from "react-redux";
import { Chat as ChatType } from "enums/ChatType";
import "./Chat.scss";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { sseService } from "services/SSEStream";
import { isMobile } from "react-device-detect";
import { DomainState } from "redux-context/domain";
import { formatBookingDate } from "helpers/messenger";
import { FoodieMessage } from "components/messenger/FoodieMessage";
import { BusinessMessage } from "components/messenger/BusinessMessage";
import { MustardMessage } from "components/messenger/MustardMessage";
import { ActionMessage } from "components/messenger/ActionMessage";
import { SystemMessage } from "components/messenger/SystemMessage";

export const getParticipantsByType = (chat: ChatType) => {
  const foodie = chat.participants.find(
    (participant) => participant.participantType === "foodie"
  );
  const business = chat.participants.find(
    (participant) => participant.participantType === "business"
  );

  return {
    foodie,
    business,
  };
};

const createDummyMessage = ({
  content,
  loggedUser,
  senderType,
}: {
  content: string;
  loggedUser: any;
  senderType: "business" | "mustard" | "mustard-on-behalf-of-business";
}) => {
  return {
    content,
    messageType: "text",
    state: "sending",
    senderType,
    sender: {
      _id: loggedUser?._id,
      name: senderType === "mustard" ? "Mustard" : loggedUser.name,
      avatarUrl:
        senderType === "mustard"
          ? "https://app-collabs.mustard.love/logo192.png"
          : loggedUser.avatarUrl,
    },
    _id: `pending-${Date.now().toString()}`,
    createdAt: new Date().toISOString(),
  };
};

export default function Chat({ id }: { id: string }) {
  const businessId = localStorage.getItem("businessId");
  const { metadata } = useSelector((state: DomainState) => state.domain);
  const [chat, setChat] = useState<ChatType | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const isAdminUser = loggedUser?.email?.endsWith(`@${metadata?.domain}`);
  const [senderType, setSenderType] = useState<
    "mustard" | "mustard-on-behalf-of-business" | "business"
  >();

  const newMessageSubscribeRef = useRef<() => void>();
  const deleteMessageSubscribeRef = useRef<() => void>();

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }, 0);
  }, [messagesContainerRef]);

  useEffect(() => {
    if (!id) return;
    scrollToBottom();
  }, [id, scrollToBottom]);

  const markAsRead = useCallback(async () => {
    if (!id || !businessId) return;

    try {
      console.log("marking chat as read");
      await markChatAsRead(id, businessId);
    } catch (error) {
      //ignore
    }
  }, [id, businessId]);

  useEffect(() => {
    newMessageSubscribeRef.current = sseService.subscribe(
      isAdminUser ? "superchat/new-message" : "chat/new-message",
      (event) => {
        console.log("chat/new-message");
        const message = JSON.parse(event.data) as ChatType["messages"][0];

        if (message.chatId !== id) {
          console.log("skipping message");
          return;
        }

        setChat((prevChat) => {
          if (!prevChat) return prevChat;

          const messagesWithoutPendingAndDuplicate = prevChat.messages.filter(
            (msg) => !msg._id.startsWith("pending-") && msg._id !== message._id
          );

          return {
            ...prevChat,
            messages: [...messagesWithoutPendingAndDuplicate, message],
          };
        });

        scrollToBottom();
        markAsRead();
      }
    );

    deleteMessageSubscribeRef.current = sseService.subscribe(
      isAdminUser
        ? "superchat/delete-message"
        : "chat/mustard-as-business/delete-message",
      (event) => {
        console.log("chat/delete-message");
        const data = JSON.parse(event.data) as {
          chatId: string;
          messageId: string;
        };

        if (data.chatId !== id) {
          console.log("skipping message");
          return;
        }

        setChat((prevChat) => {
          if (!prevChat) return prevChat;
          return {
            ...prevChat,
            messages: prevChat.messages.filter(
              (msg) => msg._id !== data.messageId
            ),
          };
        });
      }
    );

    return () => {
      newMessageSubscribeRef.current?.();
      deleteMessageSubscribeRef.current?.();
    };
  }, [id, markAsRead, scrollToBottom, isAdminUser]);

  useEffect(() => {
    if (isAdminUser === undefined || senderType) return;

    if (isAdminUser) {
      setSenderType("mustard-on-behalf-of-business");
    } else {
      setSenderType("business");
    }
  }, [isAdminUser, businessId, senderType]);

  const businessParticipant = chat?.participants.find(
    (participant) => participant.participantId === businessId
  );
  useEffect(() => {
    const abortController = new AbortController();
    if (id && businessId) {
      setLoading(true);
      getChat(id, businessId, abortController)
        .then((res) => {
          //@ts-ignore
          setChat(res);
          //@ts-ignore
          if (isAdminUser && res.context.type === "business-mustard") {
            setSenderType("mustard");
          }
        })
        .then(() => {
          scrollToBottom();
          markAsRead();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      abortController.abort();
    };
  }, [businessId, id, markAsRead, scrollToBottom, isAdminUser]);

  const handleSendMessage = async () => {
    if (!chat || !message || isSendingMessage || message.trim().length === 0)
      return;

    const originalMessage = message;
    setMessage("");

    try {
      setIsSendingMessage(true);

      setChat((prevChat) => {
        if (!prevChat) return prevChat;

        return {
          ...prevChat,
          messages: [
            ...prevChat.messages,
            createDummyMessage({
              content: originalMessage,
              loggedUser: businessParticipant,
              senderType: senderType ?? "business",
            }) as ChatType["messages"][number],
          ],
        };
      });

      scrollToBottom();

      if (!businessParticipant?.participantId || !businessId || !senderType) {
        return;
      }

      //@ts-ignore
      const messageFromServer: ChatType["messages"][number] = await sendMessage(
        chat._id,
        {
          message: originalMessage,
          senderType,
          sauceUserId: loggedUser?.id,
          businessId,
        }
      );

      setChat((prevChat) => {
        if (!prevChat) return prevChat;

        const messagesWithoutPendingAndDuplicate: ChatType["messages"] =
          prevChat?.messages.filter(
            (msg) =>
              !msg._id.startsWith("pending-") &&
              msg._id !== messageFromServer._id
          ) ?? [];

        return {
          ...prevChat,
          messages: [...messagesWithoutPendingAndDuplicate, messageFromServer],
        };
      });

      scrollToBottom();
    } catch (error) {
      console.error(error);
      setChat({
        ...chat,
        messages: [
          ...chat.messages,
          {
            ...(createDummyMessage({
              content: originalMessage,
              loggedUser: businessParticipant,
              senderType: senderType ?? "business",
            }) as ChatType["messages"][number]),
            state: "error",
          },
        ],
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (!chat || loading) {
    return (
      <div className={`chat ${isMobile ? "--mobile" : ""} --loading`}>
        <Spin />
      </div>
    );
  }

  const { foodie, business } = getParticipantsByType(chat);

  const isDisabled =
    chat.context.type === "collab" &&
    [6, 7, 8, 12].includes(chat.context.booking?.status ?? -1);

  const handleDeleteMessage = async (message: ChatType["messages"][number]) => {
    try {
      await deleteMessage(chat._id, message._id);
      setChat((prevChat) => {
        if (!prevChat) return prevChat;
        return {
          ...prevChat,
          messages: prevChat.messages.filter((msg) => msg._id !== message._id),
        };
      });
    } catch (error) {
      setChat((prevChat) => {
        if (!prevChat) return prevChat;
        const messageIndex = prevChat.messages.findIndex(
          (msg) => msg._id === message._id
        );
        const messageToUpdate = prevChat.messages[messageIndex];

        const newMessage = {
          ...messageToUpdate,
          canDelete: false,
        } as ChatType["messages"][number];
        prevChat.messages[messageIndex] = newMessage;

        return {
          ...prevChat,
          messages: [...prevChat.messages],
        };
      });
      window.alert("Failed to delete message because it was already read");
    }
  };

  return (
    <div className={`chat ${isMobile ? "--mobile" : ""}`}>
      <div className="chat__card">
        <div className="chat__header">
          <div className="chat__header-content">
            <div className="chat__header-avatars">
              {chat.context.type === "collab" && (
                <>
                  <Avatar src={foodie?.avatarUrl} size={isMobile ? 32 : 40} />
                  <Avatar
                    src={business?.avatarUrl}
                    size={isMobile ? 32 : 40}
                    style={{ marginLeft: -10 }}
                  />
                </>
              )}
              {chat.context.type === "business-mustard" && (
                <>
                  <div className="mustard">
                    <Avatar
                      src={metadata?.smallLogoUrl}
                      size={isMobile ? 32 : 40}
                    />
                  </div>
                  <Avatar
                    src={business?.avatarUrl}
                    size={isMobile ? 32 : 40}
                    style={{ marginLeft: -10 }}
                  />
                </>
              )}
            </div>
            <div>
              <div className="chat__header-names">
                <div>
                  <span className="chat__header-name">
                    {chat.context.type === "collab" && (
                      <>
                        {foodie?.participantName}{" "}
                        <span style={{ fontWeight: 400 }}>and</span>{" "}
                        {business?.participantName}
                      </>
                    )}
                    {chat.context.type === "business-mustard" && (
                      <>Mustard support for {business?.participantName}</>
                    )}
                  </span>
                </div>
                <div>
                  {chat.context.booking?.bookDate ? (
                    <>· {formatBookingDate(chat.context.booking.bookDate)}</>
                  ) : (
                    ""
                  )}
                </div>
                <div>
                  {chat.context.booking?.bookHour ? (
                    <>· {chat.context.booking.bookHour}</>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div className="chat__header__context-info">
                <span className="chat__header__context-item">
                  {chat.context.type === "collab" && (
                    <>
                      <LocationIcon className="icon" />
                      <span>{chat.context.venue?.address ?? "-"}</span>
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="chat__messages" ref={messagesContainerRef}>
          <div className="chat__messages-container">
            <div>
              {chat.messages.map((message) => (
                <React.Fragment key={message._id}>
                  {message.senderType === "foodie" && (
                    <FoodieMessage key={message._id} {...message} />
                  )}
                  {message.senderType === "business" && (
                    <BusinessMessage key={message._id} {...message} />
                  )}
                  {message.senderType === "mustard-on-behalf-of-business" && (
                    <BusinessMessage
                      key={message._id}
                      {...message}
                      onDeleteClick={() => handleDeleteMessage(message)}
                    />
                  )}
                  {message.senderType === "mustard" &&
                    message.messageType === "text" && (
                      <MustardMessage
                        key={message._id}
                        {...message}
                        onDeleteClick={() => handleDeleteMessage(message)}
                      />
                    )}
                  {message.senderType === "mustard" &&
                    message.messageType === "business-action" && (
                      <ActionMessage
                        key={message._id}
                        {...message}
                        hideActions={isDisabled}
                      />
                    )}
                  {![
                    "foodie",
                    "business",
                    "mustard",
                    "mustard-on-behalf-of-business",
                  ].includes(message.senderType) && (
                    <>
                      <div>Type: {message.senderType}</div>
                      <div className="card">{message.content}</div>
                    </>
                  )}
                </React.Fragment>
              ))}
              {isDisabled && (
                <SystemMessage text="This chat is no longer available." />
              )}
            </div>
          </div>
        </div>
        <div className="chat__input-container">
          <Mentions
            value={message}
            onChange={(value) => setMessage(value)}
            placeholder="Type your message here..."
            className="chat__input-textarea"
            disabled={isDisabled}
            onPressEnter={handleSendMessage}
            placement="top"
            // onKeyDown={(e) => {
            //   if (e.key === "Enter") {
            //     e.preventDefault();
            //     handleSendMessage();
            //   }
            // }}
            options={[
              { value: "mustard", label: "@Mustard", style: { color: "blue" } },
            ]}
          />
          <div className="chat__input-actions">
            <div className="chat__input-buttons">
              <Button
                type="primary"
                className="chat__input-send"
                disabled={isSendingMessage || isDisabled}
                onClick={handleSendMessage}
              >
                <SendOutlined className="chat__input-send-icon" />
              </Button>
              {isAdminUser && !isDisabled && (
                <Select
                  labelInValue={false}
                  suffixIcon={null}
                  className="chat__input-select"
                  dropdownClassName="chat__input-select-dropdown"
                  value={senderType}
                  onChange={(value) => setSenderType(value)}
                  options={[
                    {
                      label: <Avatar src={businessParticipant?.avatarUrl} />,
                      value: "mustard-on-behalf-of-business",
                    },
                    {
                      value: "mustard",
                      label: (
                        <div className="avatar">
                          <Avatar src={metadata?.smallLogoUrl} />
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
